import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Box, Button, Container, Group, Loader, Modal, SimpleGrid, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { fromMantineDate } from '../../helpers/mantineDates';
import { Timestamp, collectionGroup, doc, getAggregateFromServer, query, sum, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { ConfigContext, GlobalDataContext } from '../../contexts';
import { BaseResult, Fund, StartClosedEndedFundRequest } from '../../types';
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import 'mantine-react-table/styles.css';
import { format } from 'date-fns';
import { IconAlertTriangle, IconCheck, IconPlayerPlay, IconRefresh, IconX } from '@tabler/icons-react';

type DateLike = Timestamp | Date | string | number | { toDate?: () => Date } | null | undefined;

type FundWithAggregates = Fund & {
    // total: number;
    contributions: number;
    redemptions: number;
    totalDividends: number;
    totalRemaining: number;
};

const toDate = (value: DateLike): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const withToDate = value as { toDate?: () => Date };
    if (typeof withToDate?.toDate === 'function') {
        const parsed = withToDate.toDate();
        return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }

    return null;
};
//format the numbers  to 100,000,000.00
const formatAmount = (value?: number): string => {
    const safeValue = Number.isFinite(value) ? Number(value) : 0;
    return safeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


// Helper function to get the effective date for sorting and display
const getEffectiveDisplayDate = (fund: Fund): { date: Date | null, source: 'latest_deal_date' | 'closed_ended_start_date' | 'nav_date' | 'none' } => {
    let effectiveTimestamp: Timestamp | null = null;
    let source: 'latest_deal_date' | 'closed_ended_start_date' | 'nav_date' | 'none' = 'none';

    if (fund.latest_deal_date && typeof fund.latest_deal_date === 'object') {
        const timestamps = Object.values(fund.latest_deal_date)
            .filter(ts => ts instanceof Timestamp) as Timestamp[];
        if (timestamps.length > 0) {
            effectiveTimestamp = timestamps.reduce((max, current) =>
                (current.toMillis() > max.toMillis() ? current : max), timestamps[0]
            );
            source = 'latest_deal_date';
        }
    }

    if (!effectiveTimestamp && fund.closed_ended_start_date instanceof Timestamp) {
        effectiveTimestamp = fund.closed_ended_start_date;
        source = 'closed_ended_start_date';
    }

    if (!effectiveTimestamp && fund.nav_date instanceof Timestamp) {
        effectiveTimestamp = fund.nav_date;
        source = 'nav_date';
    }

    return { date: effectiveTimestamp ? effectiveTimestamp.toDate() : null, source };
};

const hasPendingUpdate = (fund: Fund): boolean => {
    const latestAvailableDate = toDate(fund.latest_available_date);
    const lastSyncDate = toDate(fund.lastSyncDate);

    if (!latestAvailableDate || !lastSyncDate) {
        return false;
    }

    return latestAvailableDate.getTime() > lastSyncDate.getTime();
};

const formatDisplayDate = (date: Date | null) => (date ? format(date, 'MMM dd, yyyy') : '------');

const shouldShowFundInList = (fund: Fund): boolean => {
    return fund.type === 'open_ended' || fund.type === 'closed_ended';
};

const getSyncStatusColor = (syncStatus?: string): string => {
    if (syncStatus === 'SYNCING') {
        return 'orange';
    }
    if (syncStatus === 'NEED_SYNC') {
        return 'yellow';
    }
    if (syncStatus === 'ERROR') {
        return 'red';
    }
    return 'green';
};

const FundsList = () => {
    const db = useFirestore();
    const functions = useFunctions();
    const globalDataContext = useContext(GlobalDataContext);
    const fundsMap = globalDataContext?.fundsMap || {};
    const [fundTotalsMap, setFundTotalsMap] = useState<Record<string, {
        // total: number;
        contributions: number;
        redemptions: number;
        totalDividends: number;
        returnOnCapital: number;
        remaining: number;
    }>>({});
    const [fundTotalsLoading, setFundTotalsLoading] = useState<boolean>(true);
    const [startModalOpened, setStartModalOpened] = useState(false);
    const [selectedFund, setSelectedFund] = useState<FundWithAggregates | null>(null);
    const [closedEndedStartDate, setClosedEndedStartDate] = useState<Date | null>(null);
    const [startClosedEndedFundLoading, setStartClosedEndedFundLoading] = useState(false);

    const fundsArray = useMemo(() => Object.values(fundsMap).filter(shouldShowFundInList), [fundsMap]);
    const selectedFundMissingClosedEndedConfig = useMemo(() => {
        if (!selectedFund) {
            return [];
        }

        const missingConfig: string[] = [];
        if (!selectedFund.nav_per_unit) {
            missingConfig.push('NAV/Unit');
        }
        if (!selectedFund.par_value) {
            missingConfig.push('Par value');
        }
        if (!selectedFund.custodian_account) {
            missingConfig.push('Custodian account');
        }

        return missingConfig;
    }, [selectedFund]);

    const openStartModal = (fund: FundWithAggregates) => {
        setSelectedFund(fund);
        setClosedEndedStartDate(toDate(fund.closed_ended_start_date) ?? new Date());
        setStartModalOpened(true);
    };

    const closeStartModal = (force = false) => {
        if (startClosedEndedFundLoading && !force) {
            return;
        }
        setStartModalOpened(false);
        setSelectedFund(null);
        setClosedEndedStartDate(null);
    };

    const handleStartClosedEndedFund = async () => {
        if (!selectedFund?.id || !closedEndedStartDate) {
            return;
        }

        setStartClosedEndedFundLoading(true);
        const notificationID = notifications.show({
            loading: true,
            title: `Starting ${selectedFund.name_en}`,
            message: 'Queuing closed-ended subscriptions for Geneva/GWI.',
            autoClose: false,
            withCloseButton: false,
        });

        try {
            const startClosedEndedFund = httpsCallable<StartClosedEndedFundRequest, BaseResult>(functions, 'startClosedEndedFund');
            const result = await startClosedEndedFund({
                fundID: String(selectedFund.id),
                startDate: format(closedEndedStartDate, 'yyyy-MM-dd'),
            });

            const summary = result.data?.data as {
                startedOrders?: number;
                skippedOrders?: number;
                failedOrders?: number;
            } | undefined;

            const summaryParts = [
                typeof summary?.startedOrders === 'number' ? `${summary.startedOrders} started` : undefined,
                typeof summary?.skippedOrders === 'number' && summary.skippedOrders > 0 ? `${summary.skippedOrders} skipped` : undefined,
                typeof summary?.failedOrders === 'number' && summary.failedOrders > 0 ? `${summary.failedOrders} failed` : undefined,
            ].filter(Boolean);

            notifications.update({
                id: notificationID,
                color: 'teal',
                title: `Started ${selectedFund.name_en}`,
                message: summaryParts.length ? `${summaryParts.join(', ')}.` : (result.data?.message || 'Closed-ended fund start queued.'),
                icon: <IconCheck size={18} />,
                loading: false,
                autoClose: 4000,
            });

            closeStartModal(true);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Please try again later.';
            notifications.update({
                id: notificationID,
                color: 'red',
                title: `Failed to start ${selectedFund.name_en}`,
                message: errorMessage,
                icon: <IconX size={18} />,
                loading: false,
                autoClose: 5000,
            });
        } finally {
            setStartClosedEndedFundLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const fetchTotals = async () => {
            if (!fundsArray.length) {
                if (!cancelled) {
                    setFundTotalsMap({});
                    setFundTotalsLoading(false);
                }
                return;
            }

            setFundTotalsLoading(true);

            try {
                const results =
                    // await Promise.all(
                    await Promise.allSettled(
                        fundsArray.map(async (fund) => {
                            const fundKey = (fund.code || String(fund.id));
                            if (!fundKey) {
                                return null;
                            }

                            const q = query(
                                collectionGroup(db, 'fund_data'),
                                where('gl_code', '==', fundKey)
                            );

                            const snapshot = await getAggregateFromServer(q, {
                                totalUnits: sum('eunits'),
                                totalContribution: sum('total_subscriptions'),
                                totalRedemptions: sum('total_redemptions.other'),
                                totalDividends: sum('total_redemptions.pnl'), //Dividends
                                totalROC: sum('total_redemptions.roc'),

                            });

                            const aggregateData = snapshot.data();

                            const totalUnits = aggregateData.totalUnits ?? 0;
                            const totalContribution = aggregateData.totalContribution ?? 0;
                            const totalDividends = aggregateData.totalDividends ?? 0;
                            const totalRedemptions = aggregateData.totalRedemptions ?? 0;
                            const totalReturnOnCapital = aggregateData.totalROC ?? 0;
                            // const totalValueInFundCurrency = fund.type === 'open_ended'
                            //     ? totalUnits * (fund.nav_per_unit ?? 0)
                            //     : totalContribution;
                            const amount = fund?.type === 'open_ended' ? (totalUnits ?? 0) * (fund?.nav_per_unit ?? 0) : totalContribution ?? 0;
                            return {
                                key: fundKey,
                                totals: {
                                    // total: totalValueInFundCurrency,
                                    contributions: totalContribution,
                                    redemptions: totalRedemptions,
                                    totalDividends: totalDividends,
                                    returnOnCapital: totalReturnOnCapital,
                                    remaining: (amount - totalReturnOnCapital),
                                },
                            };
                        })
                    );

                const nextMap: Record<string, {
                    // total: number;
                    contributions: number;
                    redemptions: number;
                    totalDividends: number;
                    returnOnCapital: number;
                    remaining: number;
                }> = {};


                // results.forEach((item) => {
                //     if (item) {
                //         nextMap[item.key] = item.totals;
                //     }
                // });
                // if (!cancelled) {
                //     setFundTotalsMap(nextMap);
                // }

                results.forEach((result, index) => {
                    if (result.status === 'fulfilled') {
                        const item = result.value;
                        if (item) {
                            nextMap[item.key] = item.totals;
                        }
                        return;
                    }

                    const fund = fundsArray[index];
                    const fundIdentifier = fund?.code || String(fund?.id ?? index);
                    console.error(`Error aggregating fund_data totals for fund ${fundIdentifier}:`, result.reason);
                });

                if (!cancelled) {
                    setFundTotalsMap(nextMap);
                }
            } catch (error) {
                console.error('Unexpected error while aggregating fund_data totals:', error);
            } finally {
                if (!cancelled) {
                    setFundTotalsLoading(false);
                }
            }
        };

        fetchTotals();

        return () => {
            cancelled = true;
        };
    }, []);

    // Convert fundsMap to array and memoize
    const fundsData = useMemo<FundWithAggregates[]>(() => {
        return fundsArray.map((fund) => {
            const fundKey = (fund.code || String(fund.id));
            const totals = fundTotalsMap[fundKey] ?? {
                // total: 0,
                contributions: 0,
                redemptions: 0,
                totalDividends: 0,
                returnOnCapital: 0,
                remaining: 0,
            };

            return {
                ...fund,
                // total: totals.total,
                contributions: totals.contributions,
                redemptions: totals.redemptions,
                totalDividends: totals.totalDividends,
                totalROC: totals.returnOnCapital,
                totalRemaining: totals.remaining,
            };
        });
    }, [fundTotalsMap, fundsArray]);

    const columns = useMemo<MRT_ColumnDef<FundWithAggregates>[]>(
        () => [
            {
                id: 'name',
                header: 'Name',
                size: 300,
                enablePinning: true,

                Cell: ({ row }) => {
                    const fund = row.original;
                    if (fund.name_en && typeof fund.name_en === 'string' && fund.name_en.trim() !== '') {
                        return <Text fw={500}>{fund.name_en}</Text>;
                    } else if (fund.name && typeof fund.name === 'string' && fund.name.trim() !== '') {
                        return (
                            <Group gap="xs" wrap="nowrap">
                                <Text c="orange" fs="italic">{fund.name}</Text>
                                <Tooltip label="'name_en' is missing, using 'name' field." withArrow position="top-start">
                                    <IconAlertTriangle size={16} style={{ color: 'orange' }} />
                                </Tooltip>
                            </Group>
                        );
                    }
                    return <Text c="dimmed" fs="italic">Name not available</Text>;
                },
            },
            {
                accessorKey: 'id',
                header: 'ID',
                size: 80,
                Cell: ({ cell }) => <Text ff="monospace">{String(cell.getValue() ?? 'N/A')}</Text>,
            },
            {
                accessorKey: 'type',
                header: 'Type',
                size: 120,
                Cell: ({ cell }) => {
                    const rawType = String(cell.getValue() ?? '');
                    const label = rawType ? rawType.replace(/_/g, ' ').toUpperCase() : 'N/A';
                    return (
                        <Badge color={rawType === 'open_ended' ? 'green' : 'indigo'} variant="light" radius="sm">
                            {label}
                        </Badge>
                    );
                },
            },
            {
                id: 'latestDealDate',
                header: 'Latest Deal Date',
                size: 220,
                sortingFn: (rowA, rowB) => {
                    const dateA = getEffectiveDisplayDate(rowA.original).date;
                    const dateB = getEffectiveDisplayDate(rowB.original).date;
                    // Handle null dates for sorting: place them at the end (or beginning based on preference)
                    if (dateA === null && dateB === null) return 0;
                    if (dateA === null) return 1; // Sort nulls after non-nulls
                    if (dateB === null) return -1; // Sort nulls after non-nulls
                    return dateA.getTime() - dateB.getTime();
                },
                Cell: ({ row }) => {
                    const fund = row.original;
                    const dealDatesMap = fund.latest_deal_date;

                    if (dealDatesMap && typeof dealDatesMap === 'object' && Object.keys(dealDatesMap).length > 0) {
                        return (
                            <Box style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                <SimpleGrid cols={2} spacing="xs" verticalSpacing="xs">
                                    {Object.entries(dealDatesMap).map(([key, tsValue]) => {
                                        if (tsValue instanceof Timestamp) {
                                            return (
                                                <React.Fragment key={key}>
                                                    <Text size="xs" fw={500} ta="right">{key}:</Text>
                                                    <Text size="xs">{format(tsValue.toDate(), 'MMM dd, yyyy')}</Text>
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <React.Fragment key={key}>
                                                <Text size="xs" fw={500} ta="right" c="dimmed">{key}:</Text>
                                                <Text size="xs" c="dimmed">Invalid Date</Text>
                                            </React.Fragment>
                                        );
                                    })}
                                </SimpleGrid>
                            </Box>
                        );
                    }

                    if (fund.closed_ended_start_date instanceof Timestamp) {
                        return <Text size="xs">{format(fund.closed_ended_start_date.toDate(), 'MMM dd, yyyy')} (start_date)</Text>;
                    }

                    if (fund.nav_date instanceof Timestamp) {
                        return <Text size="xs">{format(fund.nav_date.toDate(), 'MMM dd, yyyy')} (nav_date)</Text>;
                    }

                    return <Text c="dimmed" fs="italic">N/A</Text>;
                },
            },
            {
                accessorKey: 'asset_class',
                header: 'Asset Class',
                Cell: ({ cell }) => {
                    const assetClasses = cell.getValue<string[] | undefined>();
                    if (!assetClasses || assetClasses.length === 0) return <Text fs="italic">N/A</Text>;
                    return (
                        <Group gap="xs">
                            {assetClasses.map((ac) => (
                                <Badge key={ac} variant="light" color="blue" radius="sm">
                                    {ac}
                                </Badge>
                            ))}
                        </Group>
                    );
                },
            },
            {
                accessorKey: 'currency',
                header: 'Currency',
                size: 80,
                Cell: ({ cell }) => <Text fw={500}>{String(cell.getValue() ?? 'N/A').toUpperCase()}</Text>,
            },
            {
                accessorKey: 'nav_per_unit',
                header: 'NAV/Unit',
                size: 120,
                Cell: ({ cell }) => {
                    const val = cell.getValue<number | undefined>();
                    return <Text ff="monospace">{val?.toFixed(4) ?? 'N/A'}</Text>;
                },
            },

            {
                accessorKey: 'contributions',
                header: 'Total Contributions',
                size: 160,
                Cell: ({ cell }) => <Text ff="monospace">{formatAmount(cell.getValue<number>())}</Text>,
            },
            {
                accessorKey: 'redemptions',
                header: 'Total Redemptions ',
                size: 160,
                Cell: ({ cell }) => <Text ff="monospace">{formatAmount(cell.getValue<number>())}</Text>,
            },

            {
                accessorKey: 'totalDividends',
                header: 'Total Dividends ',
                size: 160,
                Cell: ({ cell }) => <Text ff="monospace">{formatAmount(cell.getValue<number>())}</Text>,
            },
            {
                accessorKey: 'totalROC',
                header: 'Total ROC ',
                size: 160,
                Cell: ({ cell }) => <Text ff="monospace">{formatAmount(cell.getValue<number>())}</Text>,
            },

            {
                accessorKey: 'totalRemaining',
                header: 'Remaining ',
                size: 150,
                Cell: ({ cell }) => <Text ff="monospace">{formatAmount(cell.getValue<number>())}</Text>,
            },
            {
                id: 'closedEndedStart',
                header: 'Closed-Ended Start',
                size: 180,
                Cell: ({ row }) => {
                    const fund = row.original;
                    if (fund.type !== 'closed_ended') {
                        return <Text c="dimmed" fs="italic">N/A</Text>;
                    }

                    const startDate = toDate(fund.closed_ended_start_date);
                    return (
                        <Stack gap={4} align="flex-start">
                            <Text size="xs" c={startDate ? undefined : 'dimmed'} fs={startDate ? undefined : 'italic'}>
                                {startDate ? format(startDate, 'MMM dd, yyyy') : 'Not set'}
                            </Text>
                            <Button
                                size="xs"
                                radius="sm"
                                variant="light"
                                color="indigo"
                                leftSection={<IconPlayerPlay size={14} />}
                                onClick={() => openStartModal(fund)}
                            >
                                {startDate ? 'Update start' : 'Start fund'}
                            </Button>
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'syncStatus',
                header: 'Sync',
                size: 120,
                Cell: ({ cell, row }) => {
                    const syncStatus = cell.getValue<string | undefined>();
                    const fund = row.original;
                    const updateAvailable = hasPendingUpdate(fund);
                    const showSyncButton = syncStatus === 'DONE' || !syncStatus || syncStatus === 'ERROR';

                    if (showSyncButton) {
                        return (
                            <Button
                                size="xs"
                                radius="sm"
                                color={updateAvailable ? 'orange' : 'gray'}
                                variant={updateAvailable ? 'filled' : 'light'}
                                leftSection={updateAvailable ? <IconRefresh size={14} /> : undefined}
                                disabled={!db}
                                onClick={async () => {
                                    try {
                                        const fundRef = doc(db, 'funds', String(fund.id));
                                        await updateDoc(fundRef, { syncStatus: "NEED_SYNC" });
                                        console.log("Document updated successfully");
                                    } catch (error) {
                                        console.error("Error updating document:", error);
                                    }
                                }}
                            >
                                {updateAvailable ? 'Update' : 'Sync'}
                            </Button>
                        );
                    }

                    return (
                        <Badge radius="sm" variant="light" color={getSyncStatusColor(syncStatus)}>
                            {syncStatus?.replace(/_/g, ' ')}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: 'auto_sync',
                header: 'Auto Sync',
                size: 120,
                Cell: ({ cell, row }) => {
                    const autoSync = cell.getValue<boolean | undefined>();
                    const fund = row.original;
                    return (
                        <Switch
                            size="sm"
                            checked={Boolean(autoSync)}
                            onChange={async (event) => {
                                try {
                                    const fundRef = doc(db, 'funds', String(fund.id));
                                    await updateDoc(fundRef, { auto_sync: event.currentTarget.checked });
                                    console.log("Document updated successfully");
                                } catch (error) {
                                    console.error("Error updating document:", error);
                                }
                            }}
                        />
                    );
                },
            },

            {
                accessorKey: 'lastSyncDate',
                header: 'Last Sync Date',
                size: 150,
                Cell: ({ row }) => {
                    const fund = row.original;
                    const latestAvailableDate = toDate(fund.latest_available_date);

                    return (
                        <Stack gap={2}>
                            <Text>{formatDisplayDate(toDate(fund.lastSyncDate))}</Text>
                            {hasPendingUpdate(fund) && (
                                <Text size="xs" c="orange" fw={500}>
                                    Latest: {formatDisplayDate(latestAvailableDate)}
                                </Text>
                            )}
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'visible',
                header: 'Show Fund',
                size: 120,
                Cell: ({ cell, row }) => {
                    const visible = cell.getValue<boolean | undefined>();
                    const fund = row.original;
                    return (
                        <Switch
                            size="sm"
                            checked={visible}
                            onChange={async (event) => {
                                try {
                                    const fundRef = doc(db, 'funds', String(fund.id));
                                    await updateDoc(fundRef, { visible: event.currentTarget.checked });
                                    console.log("Document updated successfully");
                                } catch (error) {
                                    console.error("Error updating document:", error);
                                }
                            }}
                        />
                    );
                },
            },
        ],
        [db]
    );

    const table = useMantineReactTable({
        columns,
        data: fundsData,

        enableGrouping: true,
        enableColumnDragging: false,
        enableStickyHeader: true,
        enablePinning: true,
        enableBottomToolbar: false,
        enableColumnResizing: true,
        enablePagination: false,
        enableColumnPinning: true,
        enableRowVirtualization: true,
        state: {
            columnPinning: {
                left: ['name']

            }
        },
        initialState: {
            showColumnFilters: false,
            columnPinning: {
                left: ['name',],

            },

            density: 'md',
            pagination: { pageIndex: 0, pageSize: 50 },
            columnOrder: [
                'name',
                'id',
                'type',
                'latestDealDate',
                'asset_class',
                'currency',
                'nav_per_unit',
                'total',
                'contributions',
                'redemptions',
                'totalDividends',
                'returnOnCapital',
                'totalRemaining',
                'closedEndedStart',
                'syncStatus',
                'auto_sync',
                'lastSyncDate',
                'visible'
            ]



        },

        mantinePaperProps: { shadow: 'none', withBorder: false, p: 0 }, // Thinner borders
        mantineTableProps: {
            // striped: true,
            highlightOnHover: true,
            // highlightOnHoverColor: 'yellow',
            // style: {
            //   fontSize: '12px', // Smaller font size
            // },
        },
        mantineTableBodyCellProps: {
            style: {
                padding: '2px 4px', // Reduced cell padding
            },
        },
        mantineTableHeadCellProps: {
            style: {
                padding: '4px 8px', // Reduced header padding
                fontWeight: 'bold',
            }
        },
        mantineTableBodyRowProps: ({ row }) => ({
            style: hasPendingUpdate(row.original)
                ? { backgroundColor: 'rgba(255, 198, 113, 0.14)' }
                : undefined,
        }),
    });

    if (!globalDataContext || Object.keys(fundsMap).length === 0) {
        return <Container>
            <Loader />
            <Text c="dimmed">Loading funds data or no funds available...</Text>
        </Container>
    }

    if (fundTotalsLoading) {
        return <Container>
            <Loader />
            <Text c="dimmed">Loading fund totals...</Text>
        </Container>
    }

    return (
        <Box px="md" pb="md">
            <MantineReactTable table={table} />
            <Modal
                opened={startModalOpened}
                onClose={closeStartModal}
                title={selectedFund ? `Start ${selectedFund.name_en}` : 'Start closed-ended fund'}
                closeOnClickOutside={!startClosedEndedFundLoading}
                closeOnEscape={!startClosedEndedFundLoading}
            >
                <Stack gap="md">
                    <Text size="sm">
                        This sets a closed-ended start date, queues the initial subscription journal entries, and stamps waiting subscription orders so the existing order refresh flow can create the GWI customer, wire relation, and subscription records.
                    </Text>
                    {selectedFundMissingClosedEndedConfig.length > 0 && (
                        <Alert color="yellow" title="Missing fund config">
                            {selectedFundMissingClosedEndedConfig.join(', ')}
                        </Alert>
                    )}
                    <DateInput
                        label="Start date"
                        placeholder="Select date"
                        value={closedEndedStartDate}
                        onChange={(value) => setClosedEndedStartDate(fromMantineDate(value))}
                        valueFormat="DD/MM/YYYY"
                        locale="en-gb"
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => closeStartModal()} disabled={startClosedEndedFundLoading}>
                            Cancel
                        </Button>
                        <Button
                            color="indigo"
                            onClick={handleStartClosedEndedFund}
                            loading={startClosedEndedFundLoading}
                            disabled={!selectedFund || !closedEndedStartDate || selectedFundMissingClosedEndedConfig.length > 0}
                        >
                            Start fund
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Box>
    );
};

export default FundsList;
